export type CalendarDateParts = {
  year: number;
  month: number;
  day: number;
};

const utcCalendar = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
});

/**
 * Leading YYYY-MM-DD of a date-only value, read as text.
 * The time and zone suffix are ignored so a midnight ISO form stays the same day.
 */
export function calendarDateParts(
  value: string | null | undefined,
): CalendarDateParts | null {
  if (value == null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** True for a calendar date, including the midnight form a date column serializes to. */
export function isDateOnlyValue(value: string | null | undefined): boolean {
  if (value == null) return false;
  return /^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.\d+)?(?:Z|[+-]00:?00)?)?$/.test(
    value.trim(),
  );
}

/**
 * Readable calendar date. Parts are taken from the text, then formatted with
 * timeZone UTC, so "2026-09-26" is "26 Sep" in every machine timezone.
 * Empty or unusable input returns "".
 * Overdue is not decided here. The backend sends that flag or count.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  const parts = calendarDateParts(value);
  if (!parts) return '';
  const utcDay = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const formatted = utcCalendar.formatToParts(utcDay);
  const day = formatted.find((part) => part.type === 'day')?.value;
  const month = formatted.find((part) => part.type === 'month')?.value;
  if (!day || !month) return '';
  return `${day} ${month}`;
}
